import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  fetchMarketReference,
  type MarketReferenceSector,
} from '../../../services/marketReferenceService';
import type {
  CommunityVisibilitySettings,
  UserProfileFormData,
  UserProfileState,
} from './marketProfileStore';
import { profileToAuthorProfile } from './marketProfileStore';
import { MpField, SensitiveDataNotice } from './MarketProfileShared';
import { AuthorBadge } from '../CommunityShared';

type Props = {
  profile: UserProfileState;
  onSave: (data: UserProfileFormData, visibility: CommunityVisibilitySettings) => void;
};

function authPrefill(user: ReturnType<typeof useAuth>['user']): Partial<UserProfileFormData> {
  if (!user) return {};
  const fullName = user.user_metadata?.full_name?.trim() ?? '';
  const parts = fullName.split(/\s+/).filter(Boolean);
  return {
    email: user.email ?? '',
    phone: user.phone ?? '',
    firstName: parts[0] ?? user.user_metadata?.name ?? '',
    lastName: parts.slice(1).join(' '),
    pseudonym: user.user_metadata?.display_name ?? user.user_metadata?.name ?? '',
  };
}

function CommunityPreview({ profile }: { profile: UserProfileState }) {
  const author = profileToAuthorProfile(profile);
  const metaHidden = author.sector === '—' && author.roleLevel === '—' && !author.company;

  return (
    <div className="dcf-mp-profile-preview">
      <div className="dcf-mp-profile-preview__label">Так вас увидят в сообществе</div>
      <div className="dcf-mp-profile-preview__card">
        <AuthorBadge author={author} preview />
        {metaHidden && (
          <p className="dcf-mp-profile-preview__note">Участники увидят только псевдоним</p>
        )}
      </div>
    </div>
  );
}

export default function ProfileScreen({ profile, onSave }: Props) {
  const { user } = useAuth();
  const [form, setForm] = useState<UserProfileFormData>(profile.data);
  const [visibility, setVisibility] = useState<CommunityVisibilitySettings>(profile.visibility);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [refLoading, setRefLoading] = useState(true);
  const [sectors, setSectors] = useState<MarketReferenceSector[]>([]);

  useEffect(() => {
    setForm(profile.data);
    setVisibility(profile.visibility);
  }, [profile]);

  useEffect(() => {
    const prefill = authPrefill(user);
    setForm((prev) => ({
      lastName: prev.lastName || prefill.lastName || '',
      firstName: prev.firstName || prefill.firstName || '',
      pseudonym: prev.pseudonym || prefill.pseudonym || '',
      email: prev.email || prefill.email || '',
      phone: prev.phone || prefill.phone || '',
      sectorName: prev.sectorName,
      companyName: prev.companyName,
      positionName: prev.positionName,
    }));
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRefLoading(true);
      try {
        const data = await fetchMarketReference();
        if (!cancelled) setSectors(data.sectors);
      } catch {
        if (!cancelled) setSectors([]);
      } finally {
        if (!cancelled) setRefLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const previewProfile = useMemo(
    (): UserProfileState => ({ data: form, visibility, updatedAt: null }),
    [form, visibility],
  );

  const set = <K extends keyof UserProfileFormData>(key: K, value: UserProfileFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError(null);
    setSaved(false);
  };

  const setVisibilityField = <K extends keyof CommunityVisibilitySettings>(
    key: K,
    value: CommunityVisibilitySettings[K],
  ) => {
    setVisibility((prev) => {
      if (key === 'pseudonymOnly' && value === true) {
        return { ...prev, pseudonymOnly: true };
      }
      return { ...prev, [key]: value };
    });
    setSaved(false);
  };

  const handleSave = () => {
    if (!form.pseudonym.trim()) {
      setError('Укажите псевдоним — он отображается в сообществе.');
      return;
    }
    onSave(form, visibility);
    setSaved(true);
    setError(null);
  };

  return (
    <div className="dcf-mp-profile">
      <header className="dcf-header">
        <h1 className="dcf-header__title">Профиль</h1>
        <p className="dcf-header__desc">
          Личные данные и настройки того, что другие участники увидят рядом с вашими сообщениями.
          Фамилия, имя, email и телефон в сообществе не показываются.
        </p>
      </header>

      <div className="dcf-mp-card dcf-mp-form-card">
        <h2 className="dcf-mp-form-section-title">Личные данные</h2>
        <div className="dcf-mp-form-grid">
          <MpField label="Фамилия" htmlFor="mp-profile-last">
            <input
              id="mp-profile-last"
              className="dcf-input"
              value={form.lastName}
              onChange={(e) => set('lastName', e.target.value)}
              autoComplete="family-name"
            />
          </MpField>
          <MpField label="Имя" htmlFor="mp-profile-first">
            <input
              id="mp-profile-first"
              className="dcf-input"
              value={form.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              autoComplete="given-name"
            />
          </MpField>
          <MpField label="Псевдоним" required htmlFor="mp-profile-pseudo">
            <input
              id="mp-profile-pseudo"
              className="dcf-input"
              value={form.pseudonym}
              onChange={(e) => set('pseudonym', e.target.value)}
              placeholder="Как вас видят в сообществе"
            />
          </MpField>
          <MpField label="Email" htmlFor="mp-profile-email" hint="Используется для входа в аккаунт">
            <input
              id="mp-profile-email"
              className="dcf-input dcf-input--readonly"
              type="email"
              value={form.email}
              readOnly
              autoComplete="email"
            />
          </MpField>
          <MpField label="Сектор" htmlFor="mp-profile-sector">
            <select
              id="mp-profile-sector"
              className="dcf-select"
              value={form.sectorName}
              onChange={(e) => set('sectorName', e.target.value)}
              disabled={refLoading}
            >
              <option value="">— Не указан —</option>
              {sectors.map((sector) => (
                <option key={sector.id} value={sector.name}>{sector.name}</option>
              ))}
            </select>
          </MpField>
          <MpField label="Компания" htmlFor="mp-profile-company">
            <input
              id="mp-profile-company"
              className="dcf-input"
              value={form.companyName}
              onChange={(e) => set('companyName', e.target.value)}
            />
          </MpField>
          <MpField label="Должность" htmlFor="mp-profile-position">
            <input
              id="mp-profile-position"
              className="dcf-input"
              value={form.positionName}
              onChange={(e) => set('positionName', e.target.value)}
            />
          </MpField>
          <MpField label="Номер телефона" htmlFor="mp-profile-phone">
            <input
              id="mp-profile-phone"
              className="dcf-input"
              type="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              autoComplete="tel"
            />
          </MpField>
        </div>
      </div>

      <div className="dcf-mp-card dcf-mp-form-card">
        <h2 className="dcf-mp-form-section-title">Видимость в сообществе</h2>
        <p className="dcf-mp-form-hint">
          Настройте, какие поля профиля будут видны рядом с вашими сообщениями и ответами.
        </p>

        <div className="dcf-mp-visibility-list">
          <label className="dcf-mp-visibility-row">
            <input
              type="checkbox"
              checked={visibility.pseudonymOnly}
              onChange={(e) => setVisibilityField('pseudonymOnly', e.target.checked)}
            />
            <span className="dcf-mp-visibility-row__body">
              <span className="dcf-mp-visibility-row__title">Показывать только псевдоним</span>
              <span className="dcf-mp-visibility-row__desc">
                Должность, сектор и компания скрыты для всех участников.
              </span>
            </span>
          </label>

          <label className={`dcf-mp-visibility-row ${visibility.pseudonymOnly ? 'dcf-mp-visibility-row--disabled' : ''}`}>
            <input
              type="checkbox"
              checked={visibility.showPosition}
              disabled={visibility.pseudonymOnly}
              onChange={(e) => setVisibilityField('showPosition', e.target.checked)}
            />
            <span className="dcf-mp-visibility-row__body">
              <span className="dcf-mp-visibility-row__title">Показывать должность</span>
            </span>
          </label>

          <label className={`dcf-mp-visibility-row ${visibility.pseudonymOnly ? 'dcf-mp-visibility-row--disabled' : ''}`}>
            <input
              type="checkbox"
              checked={visibility.showSector}
              disabled={visibility.pseudonymOnly}
              onChange={(e) => setVisibilityField('showSector', e.target.checked)}
            />
            <span className="dcf-mp-visibility-row__body">
              <span className="dcf-mp-visibility-row__title">Показывать сектор</span>
            </span>
          </label>

          <label className={`dcf-mp-visibility-row ${visibility.pseudonymOnly ? 'dcf-mp-visibility-row--disabled' : ''}`}>
            <input
              type="checkbox"
              checked={visibility.showCompany}
              disabled={visibility.pseudonymOnly}
              onChange={(e) => setVisibilityField('showCompany', e.target.checked)}
            />
            <span className="dcf-mp-visibility-row__body">
              <span className="dcf-mp-visibility-row__title">Показывать компанию</span>
            </span>
          </label>
        </div>

        <CommunityPreview profile={previewProfile} />
      </div>

      <SensitiveDataNotice compact />

      {error && <p className="dcf-mp-error">{error}</p>}
      {saved && !error && (
        <p className="dcf-mp-profile-saved">Профиль сохранён</p>
      )}

      <div className="dcf-mp-form-actions">
        <span />
        <div className="dcf-mp-form-actions__right">
          <button type="button" className="dcf-btn dcf-btn--primary" onClick={handleSave}>
            Сохранить профиль
          </button>
        </div>
      </div>
    </div>
  );
}
